import styled from '@emotion/styled';
import { themeGet } from '@styled-system/theme-get';

export const StyledPlanBoxHeader = styled.div`
  padding: ${themeGet('scale.scaleUp05')} ${themeGet('scale.scaleUp03')};
  font-size: ${themeGet('scale.scaleDown01')};
  text-align: center;
  border-radius: ${themeGet('border.borderRadius')}
    ${themeGet('border.borderRadius')} 0 0;

  p {
    font-size: ${themeGet('scale.scaleDown01')};
  }

  a {
    color: inherit;
  }
`;

export const StyledPlanContent = styled.div`
  padding: ${themeGet('scale.scaleUp03')} ${themeGet('scale.scaleUp03')} 0;
  text-align: center;

  .footnote {
    margin: ${themeGet('scale.scaleBase')} 0;
    font-size: 12px;
    color: ${themeGet('color.colorGrayBlue70')};
  }
`;

export const StyledPlanList = styled.ul`
  display: grid;
  grid-template-rows: repeat(8, 1fr);
  padding: 0 ${themeGet('scale.scaleUp03')} ${themeGet('scale.scaleDown01')};

  @media (max-width: ${themeGet('breakpoint.breakpointPricingLarge')}) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: auto;
  }

  @media (max-width: ${themeGet('breakpoint.breakpointTablet')}) {
    display: none;
    visibility: hidden;
  }

  @media (max-width: ${themeGet('breakpoint.breakpointMobile')}) {
    grid-template-columns: 1fr;
  }

  &.is-open {
    visibility: visible;

    @media (max-width: ${themeGet('breakpoint.breakpointTablet')}) {
      display: grid;
      grid-template-columns: 1fr 1fr;
    }

    @media (max-width: ${themeGet('breakpoint.breakpointMobile')}) {
      display: block;
    }
  }

  li {
    display: flex;
    align-items: center;
    min-height: ${themeGet('scale.scaleUp11')};
    margin: 2px 0;
    padding: 0;

    @media (max-width: ${themeGet('breakpoint.breakpointPricingLarge')}) {
      min-height: 0;
      margin: ${themeGet('scale.scaleUp02')} 0 0;
    }

    @media (max-width: ${themeGet('breakpoint.breakpointMobile')}) {
      margin-bottom: ${themeGet('scale.scaleUp02')};
    }

    &:last-of-type {
      margin-bottom: 0;
    }
  }
`;

export const StyledPlanGrid = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;

  > div {
    flex: 1 1 20%;
    margin: 0 ${themeGet('scale.scaleUp03')} ${themeGet('scale.scaleUp03')};
  }

  @supports (display: grid) {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    grid-gap: ${themeGet('scale.scaleUp02')};

    > div {
      flex: 0;
      margin: 0;
    }

    @media (max-width: ${themeGet('breakpoint.breakpointPricingLarge')}) {
      grid-template-columns: 1fr 1fr;
    }

    @media (max-width: ${themeGet('breakpoint.breakpointTablet')}) {
      grid-template-columns: 1fr;
    }
  }

  &.has-three-columns {
    > div {
      flex: 1 1 22%;
      margin: 0 ${themeGet('scale.scaleUp03')} ${themeGet('scale.scaleUp03')};
    }

    @supports (display: grid) {
      grid-template-columns: repeat(3, 1fr);

      > div {
        flex: 0;
        margin: 0;
      }

      @media (max-width: 1033px) {
        grid-template-columns: 1fr 1fr;
      }

      @media (max-width: ${themeGet('breakpoint.breakpointTablet')}) {
        grid-template-columns: 1fr;
      }
    }

    ${StyledPlanBoxHeader} {
      @media (max-width: 1033px) {
        padding-right: ${themeGet('scale.scaleUp12')};
        padding-left: ${themeGet('scale.scaleUp12')};
      }

      @media (max-width: ${themeGet('breakpoint.breakpointTablet')}) {
        padding-right: ${themeGet('scale.scaleUp03')};
        padding-left: ${themeGet('scale.scaleUp03')};
      }
    }

    ${StyledPlanList} {
      @media (max-width: ${themeGet('breakpoint.breakpointPricingLarge')}) {
        display: block;
      }

      @media (max-width: ${themeGet('breakpoint.breakpointTablet')}) {
        display: none;
      }

      &.is-open {
        @media (max-width: ${themeGet('breakpoint.breakpointTablet')}) {
          display: grid;
        }

        @media (max-width: ${themeGet('breakpoint.breakpointMobile')}) {
          display: block;
        }
      }
    }
  }
`;

export const StyledPlanBox = styled.div`
  display: grid;
  grid-template-rows: auto 1fr;
  position: relative;
  padding-bottom: ${themeGet('scale.scaleUp03')};
  background-color: ${themeGet('color.colorWhite')};
  border-radius: ${themeGet('border.borderRadius')};
  box-shadow: ${themeGet('boxShadow.boxShadowSmall')};

  @media (max-width: ${themeGet('breakpoint.breakpointTablet')}) {
    grid-template-columns: 1fr;
  }

  &.custom {
    ${StyledPlanBoxHeader} {
      background-color: ${themeGet('color.colorGrayBlue100')};
      h3, p {
        color: ${themeGet('color.colorWhite')};
      }
    }
  }

  &.advanced {
    ${StyledPlanBoxHeader} {
      background-color: ${themeGet('color.colorOrangeLightest')};

      h3 {
        color: ${themeGet('color.colorOrangeDark')};
      }
    }
  }

  &.basic {
    ${StyledPlanBoxHeader} {
      background-color: ${themeGet('color.colorVioletLightest')};

      h3 {
        color: ${themeGet('color.colorVioletDark')};
      }
    }
  }

  &.free {
    ${StyledPlanBoxHeader} {
      background-color: ${themeGet('color.colorBlueLightest')};

      h3 {
        color: ${themeGet('color.colorBlueDark')};
      }
    }
  }

  &.essentials {
    ${StyledPlanBoxHeader} {
      background-color: ${themeGet('color.colorGreenLightest')};

      h3 {
        color: ${themeGet('color.colorGreenDark')};
      }
    }
  }

  &.pro {
    ${StyledPlanBoxHeader} {
      background-color: ${themeGet('color.colorYellowLightest')};

      h3 {
        color: ${themeGet('color.colorYellowDark')};
      }
    }
  }

  &.premier {
    ${StyledPlanBoxHeader} {
      background-color: ${themeGet('color.colorBlueLightest')};

      h3 {
        color: ${themeGet('color.colorBlueDark')};
      }
    }
  }

  .tag {
    position: absolute;
    top: -12px;
    right: ${themeGet('scale.scaleUp02')};
  }

  .ei-free-icon svg,
  .ei-premier-icon svg {
    width: 108px;
    height: auto;
  }

  .icon-wrapper {
    flex: 0;
    min-width: ${themeGet('scale.scaleUp03')};
    height: ${themeGet('scale.scaleUp03')};
    margin-right: ${themeGet('scale.scaleDown03')};
    background-color: ${themeGet('color.colorGreenLightest')};
    border-radius: 50%;

    div {
      margin-top: 0;
      margin-left: ${themeGet('scale.scaleDown03')};
    }
  }

  .icon-wrapper.excluded {
    background-color: ${themeGet('color.colorPinkLightest')};

    div {
      margin-top: 0;
      margin-left: ${themeGet('scale.scaleDown03')};
    }
  }

  .plan-box-feature-hider {
    display: flex;
    align-items: center;
    margin: ${themeGet('scale.scaleUp03')} auto ${themeGet('scale.scaleDown02')};
    font-family: "Whitney SSm A", "Whitney SSm B", "Helvetica Neue", Helvetica, Arial, sans-serif;
    font-size: ${themeGet('scale.scaleDown02')};
    font-weight: ${themeGet('fontWeight.fontWeightBold')};
    text-transform: uppercase;
    color: ${themeGet('color.colorBlue')};
    background-color: transparent;
    border: 0;
    -webkit-appearance: none;

    &:hover {
      cursor: pointer;
    }

    @media (min-width: ${themeGet('breakpoint.breakpointTablet')}) {
      display: none;
    }

    .icon-wrapper {
      background-color: ${themeGet('color.colorGrayBlue30')};

      div {
        margin-top: 3px;
      }
    }

    &.is-active {
      .icon-wrapper div {
        margin-top: 4px;
        margin-left: 12px;
        transform: rotate(45deg);
      }
    }
  }
`;
